import React, { useState, useEffect, useMemo } from 'react';
import PopUp from '../../components/PopUp';
import { TextInput, Button, Divider, Grid, Select, Checkbox, MultiSelect } from '@mantine/core';
import {ReactComponent as  RemoveIcon } from '../../assets/icons/Remove.svg'
import {ReactComponent as DropdownIcon } from '../../assets/icons/Dropdown.svg'
import {useDisclosure } from '@mantine/hooks';
import { CategoryPopUpProps } from '../../types/categories';
import sorterStyles from '../sorters/SorterPage.module.css'
import MaterialsList from './MaterialsList';
import api from '../../api/axiosClient';
import { useCategories } from '../../context/CategoriesContext';

interface SimilarColorResponse {
    id: number;
    main_color_id: number;
    similar_color_id: number;
}

const CategoryPopUp = ({opened, close, title, onSave, category, categories = []}: CategoryPopUpProps) => {
    const { fetchSimilarColors: refreshSimilarColors } = useCategories();
    const [name, setName] = useState<string>('');
    const [enable, setEnable] = useState<boolean>(true);
    const [materials, setMaterials] = useState<any[]>([]);
    const [similarColorIds, setSimilarColorIds] = useState<string[]>([]);
    const [initialSimilarColorRows, setInitialSimilarColorRows] = useState<SimilarColorResponse[]>([]);

    const isColorCategory = title.includes('Color');

    useEffect(() => {
        if (opened) {
            setName(category?.name || '');
            setEnable(category?.enable !== undefined ? category.enable : true);
            setMaterials(category?.details || []);
        }
    }, [opened, category]);

    useEffect(() => {
        if (!opened || !isColorCategory) {
            return;
        }

        if (!category?.id) {
            setSimilarColorIds([]);
            setInitialSimilarColorRows([]);
            return;
        }

        const fetchSimilarColors = async () => {
            try {
                const response = await api.get<SimilarColorResponse[]>('/similar-colors');
                const rows = response.data.filter((row) => row.main_color_id === category.id);
                setInitialSimilarColorRows(rows);
                setSimilarColorIds(rows.map((row) => String(row.similar_color_id)));
            } catch (error) {
                console.error('Error fetching similar colors:', error);
                setInitialSimilarColorRows([]);
                setSimilarColorIds([]);
            }
        };

        fetchSimilarColors();
    }, [opened, category, isColorCategory]);

    const colorOptions = useMemo(() => {
        if (!isColorCategory) {
            return [];
        }

        return categories
            .filter((item) => item.id !== category?.id)
            .map((item) => ({ value: String(item.id), label: item.name }));
    }, [categories, category?.id, isColorCategory]);

    const trimmedName = name.trim();
    const duplicateNameExists = useMemo(() => {
        if (!trimmedName) {
            return false;
        }

        const normalizedName = trimmedName.toLowerCase();
        return categories.some((item) => {
            if (category?.id !== undefined && item.id === category.id) {
                return false;
            }

            return item.name.trim().toLowerCase() === normalizedName;
        });
    }, [categories, category?.id, trimmedName]);

    const nameError = !trimmedName
        ? 'Category name is required.'
        : duplicateNameExists
            ? 'Category name already exists.'
            : null;

    const syncSimilarColors = async (colorId: number) => {
        const initialIds = initialSimilarColorRows.map((row) => String(row.similar_color_id));
        const hasChanged = initialIds.length !== similarColorIds.length
            || !initialIds.every((id) => similarColorIds.includes(id));

        if (!hasChanged) {
            return;
        }

        try {
            await Promise.all(initialSimilarColorRows.map((row) => api.delete(`/similar-colors/delete/${row.id}`)));
            await Promise.all(similarColorIds.map((id) => api.post('/similar-colors/create', {
                main_color_id: colorId,
                similar_color_id: Number(id),
            })));
            await refreshSimilarColors();
        } catch (error) {
            console.error('Error updating similar colors:', error);
        }
    };

    const handleSave = async () => {
        if (nameError) {
            return;
        }

        const saved = await onSave({
            id: category?.id,
            name: trimmedName,
            enable,
            details: materials,
        });

        if (isColorCategory) {
            const savedId = category?.id ?? saved?.id;
            if (savedId !== undefined) {
                await syncSimilarColors(savedId);
            }
        }
    };

    return(
        <div>
            <PopUp opened={opened} close={close} title={title} size={700} onSave={handleSave}>
                <div className={sorterStyles.PopupInputs}>
                    <TextInput
                        label="Category Name"
                        value={name}
                        onChange={(event) => setName(event.currentTarget.value)}
                        placeholder="Category Name"
                        error={nameError}
                    />
                    {title.includes("Recycling") && (
                        <div>
                            <Checkbox
                                label="Enable"
                                checked={enable}
                                onChange={(event) => setEnable(event.currentTarget.checked)}
                            />
                            <MaterialsList details={materials} onChange={setMaterials}></MaterialsList>
                        </div>
                    )}
                    {isColorCategory && (
                        <MultiSelect
                            label="Similar Colors"
                            placeholder="Pick similar colors"
                            data={colorOptions}
                            searchable
                            value={similarColorIds}
                            onChange={setSimilarColorIds}
                        />
                    )}
                </div>
                
            </PopUp> 
        </div>
    )
}

export default CategoryPopUp;